// PDP-only enhancement for the Rebuy recommendation carousel.
//
// The Rebuy widget renders a horizontally-scrolling product grid
// (.rebuy-product-grid) as a native overflow-x scroller. On a tall PDP that can
// swallow vertical page scrolling. Rather than patch overscroll behaviour, we
// convert the grid into an Embla carousel: Embla drives horizontal movement via
// pointer dragging, which only captures horizontal intent, so vertical page
// scrolling is never blocked (the same reason the theme uses Embla elsewhere).
//
// The widget is injected by an app block and can re-render (e.g. on variant
// change), so we watch the DOM and (re)initialise as grids appear/disappear
// rather than relying on the element existing at load. Mutations are coalesced
// into one sweep per frame — see sweep().

const GRID_SELECTOR = ".rebuy-product-grid";
const VIEWPORT_CLASS = "rebuy-embla-viewport";
const PROCESSED_ATTR = "data-rebuy-embla";

// viewport element -> Embla instance, so we can tear down on re-render.
const instances = new Map();

async function mountGrid(grid) {
  if (!(grid instanceof Element) || grid.hasAttribute(PROCESSED_ATTR)) return;
  // Claim it synchronously so a concurrent DOM mutation can't double-mount.
  grid.setAttribute(PROCESSED_ATTR, "");

  let EmblaCarousel;
  try {
    ({ default: EmblaCarousel } = await import("embla-carousel"));
  } catch (err) {
    grid.removeAttribute(PROCESSED_ATTR);
    console.warn("Rebuy Embla: failed to load embla-carousel.", err);
    return;
  }

  // Rebuy may have re-rendered while the chunk loaded.
  if (!grid.isConnected) {
    grid.removeAttribute(PROCESSED_ATTR);
    return;
  }

  // Embla's contract: an overflow-hidden viewport whose first child is the
  // track it transforms. Wrap the grid so the grid becomes that track and the
  // .rebuy-product-block children become the slides.
  const viewport = document.createElement("div");
  viewport.className = VIEWPORT_CLASS;
  grid.parentNode.insertBefore(viewport, grid);
  viewport.appendChild(grid);

  const embla = EmblaCarousel(viewport, {
    align: "start",
    containScroll: "trimSnaps",
    dragFree: true, // free momentum scroll, matching the native feel it replaces
    watchResize: true,
    watchSlides: true
  });

  instances.set(viewport, embla);
}

function unmountViewport(viewport) {
  const embla = instances.get(viewport);
  if (!embla) return;
  embla.destroy();
  instances.delete(viewport);
}

let sweepScheduled = false;

// One pass over the document, instead of a subtree query per added node. A variant
// change replaces ~13 blocks in a burst, so per-node work multiplied fast.
function sweep() {
  sweepScheduled = false;

  document.querySelectorAll(GRID_SELECTOR).forEach(mountGrid);

  // Detach by connectedness rather than by matching removal mutations — this also
  // catches a viewport removed by an ancestor being rewritten.
  instances.forEach((_, viewport) => {
    if (!viewport.isConnected) unmountViewport(viewport);
  });
}

function scheduleSweep() {
  if (sweepScheduled) return;
  sweepScheduled = true;
  requestAnimationFrame(sweep);
}

function onRebuyReady(event) {
  const { widget } = event.detail;
  const images = document.querySelectorAll(`#rebuy-widget-${widget.id} .rebuy-product-media img`);
  images.forEach((img) => {
    img.src = img.src.replace("_400x400", "_600x600");
  });
  scheduleSweep();
}

function initRebuyEmbla() {
  // PDP only — body carries `template-product`, `template-product.<suffix>`, etc.
  if (!document.body || !document.body.className.includes("template-product")) return;

  sweep();

  document.addEventListener("rebuy.ready", onRebuyReady);

  // Rebuy injects and re-renders grids asynchronously, so watch for them. Mutations
  // only schedule a sweep; the work is coalesced to once per frame.
  new MutationObserver(scheduleSweep).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initRebuyEmbla, { once: true });
} else {
  initRebuyEmbla();
}

export default initRebuyEmbla;
